// One ranked item as the artboards draw it (Main.dc.html, ItemStates.dc.html; U1, U5, U6),
// with its states: the park chooser (a time, never a reason, D65), delegate (a person and a
// date, D67), the why panel with its four adjustments (D30, D121), and complete against
// dismiss side by side with what each teaches (D70). Every action posts one decision to
// POST /api/triage/items/:id/decide (the milestone's contract) and the card removes or
// updates itself in place; nothing is marked complete by inference (D60) and every outward
// option's note says the user sends it (D69).
import { CardFrame, OptionButton, ActionRow, LineOrName, Chip, ActionButton, ActionInput, ActionSelect } from './parts/index.js';
import { useViewing } from './view-as.js';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { ApiError, Unauthenticated, triage, type DecideBody, type DecideResult, type Option, type PassItem, type TeamMember } from './api.js';
import { formatDate, formatWhen } from './labels.js';
import { kindWord } from './records/shared.js';
import { ADJUSTMENTS, judgedBy, parkPresets, standingActions, STANDING_WORDS, type Adjustment, type Standing } from './ranking.js';
import { CLAIM_KINDS, claimWords, saidAfter } from './claim.js';
import { AlsoWith, ClaimPills } from './claim-parts.js';
import { PromptBox } from './reply/PromptBox.js';
import { TypedReply } from './reply/TypedReply.js';
import { instructionOf, replyDrafts, typedReply } from './reply/api.js';
import { answeredWord, DeclineBox, ProposeBox, type InviteOutcome } from './reply/InviteAnswer.js';
import { BlockBox, booked, bookedWord, undoBlock } from './reply/BlockTime.js';
import { callSaid, openCallWindow, placeCall, VIDEO_CALL } from './call/api.js';
import { callStepOf, type CallStep } from './call/read-api.js';
import { dayMonth, dayMonthYearTime } from './day.js';
import { useCompanyLine } from './company/lines.js';
import { channelOf as replyChannelOf, channelWords, chatOf, peopleWord } from './reply/channel.js';
import { backOf, BACK_CHOICES, changedWords, HAND_ON_KINDS, sinceWords, type BackChoice } from './rotation.js';
import { CardMessage, useCardThread } from './reply/CardMessage.js';
import { canReplyAll, ReplyBox } from './reply/ReplyBox.js';

/**
 * "Reply in my voice" (actions contract row 2): the one draft that opens the instruction box first. The API's
 * option engine marks it (rank/options.ts voiceOption); every other draft, and chase, go on as they did.
 */
export function isVoiceOption(o: Option): boolean {
  return o.kind === 'draft' && (o.params as { voice?: unknown } | undefined)?.voice === true;
}

/**
 * One of Cobrain's own options, the ones it writes for him to read and send (actions contract row 1, Mike's choice of
 * 2026-09-24, answer 27): a draft, a chase, "Reply in my voice", and an `other` that carries a purpose ("Ask Conor what
 * they need from you"). Pressing one opens a box with its words in it, and nothing is recorded until the draft is sent
 * (REQ-2026-09-24-05): the press used to record the decision first, so a card left unsent had gone from the list.
 */
export function writesForYou(o: Option): boolean {
  if (o.kind === 'draft' || o.kind === 'chase') return true;
  return o.kind === 'other' && typeof (o.params as { purpose?: unknown } | undefined)?.purpose === 'string';
}

/**
 * Where an Open or a Read goes (actions contract row 7, Mike's choice of 2026-09-24, answer 8, "Go to what it names"):
 * "Each opens exactly what its label says (the contract, the deal, the thread, the article, the mails), inside Cobrain."
 * The option's own params name the record; a message (the thread, what you sent) is read on the item's own page, where
 * the evidence is. Before this, every Open but a client's went to the card's own page and Read opened nothing.
 */
export function whereItGoes(item: Pick<PassItem, 'id' | 'store' | 'subjectKind' | 'subjectId'>, o: Option): string {
  const p = (o.params ?? {}) as Record<string, unknown>;
  const q = `?store=${encodeURIComponent(item.store)}`;
  const id = (k: string): string | null => (typeof p[k] === 'string' && p[k] ? (p[k] as string) : null);
  // BUGS.md T26 (module 04 B2201): "Write your own notes while it is fresh", on a call Cobrain did not get into, opens Your
  // notes, as Mike chose by pop-up on 6 Oct.
  if (p['intent'] === 'meeting_notes') return `/deals/notes${q}`;
  // BUGS.md T19 (module 04 B2199): an agreement's own renewal card opens its company record at its agreements (Mike by
  // pop-up, 6 Oct), a section of its own since pipeline RC4.
  if (id('organisationId')) return `/companies/${encodeURIComponent(id('organisationId')!)}${q}${p['at'] === 'agreements' ? '#a-agreements' : ''}`;
  if (id('contractId')) return `/contracts/${encodeURIComponent(id('contractId')!)}${q}`;
  if (id('dealId')) return `/deals/${encodeURIComponent(id('dealId')!)}${q}`;
  if (id('matterId')) return `/matters/${encodeURIComponent(id('matterId')!)}${q}`;
  if (o.kind === 'read' && item.subjectKind === 'news_item') return `/intelligence/industry/${encodeURIComponent(item.subjectId)}${q}`;
  const page = `/items/${encodeURIComponent(item.id)}${q}`;
  return id('signalId') ? `${page}&signal=${encodeURIComponent(id('signalId')!)}#evidence` : page;
}

/** The first person an option addresses its draft to (`params.to`, BUGS.md T26), by name, else address; null when none. */
function toOf(params: unknown): string | null {
  const to = (params as { to?: unknown } | undefined)?.to;
  const first = Array.isArray(to) ? (to[0] as { name?: unknown; address?: unknown } | undefined) : undefined;
  return typeof first?.name === 'string' && first.name.trim() ? first.name.trim() : typeof first?.address === 'string' ? first.address : null;
}

/** The meeting link a Join opens (row 7): a web address on the option, never one built from a pattern. */
export function joinLink(o: Option): string | null {
  const u = (o.params as { url?: unknown } | undefined)?.url;
  return typeof u === 'string' && /^https:\/\//i.test(u) ? u : null;
}

/**
 * One line saying where the reply is going: "To Ryan Walsh, by email. You press Send." (module 10 DESIGN B1553.)
 *
 * A Teams reply posts into the chat the message came from, so in a chat of more than one other person everyone in it reads
 * it. Mike, by pop-up (REQ-2026-09-24-46, module 04 DESIGN B1831), "Name everyone": "To the Teams chat with Chris Watts and
 * Renaldo Meere. You press Send." The chat's people come on the option (rank/options.ts withChatPeople); a one-to-one chat,
 * or an option without them, reads as before.
 */
export function goingWord(item: PassItem, o: Option): string {
  const c = (o.params as { channel?: unknown } | undefined)?.channel;
  const channel = replyChannelOf({ channel: typeof c === 'string' ? c : null });
  const chat = channel === 'teams' ? chatOf(o.params) : null;
  const everyone = chat && chat.names.length + chat.others > 1 ? peopleWord(chat.names, chat.others) : null;
  if (everyone) return `To the Teams chat with ${everyone}. You press Send.`;
  // BUGS.md T26: a note the option addresses to someone ("Ask <name> for their notes") names them.
  const named = toOf(o.params);
  const where = channel === 'email' ? 'by email' : channel === 'teams' ? 'in Teams' : channel === 'messenger' ? 'in the messenger'
    : channel === 'whatsapp' ? 'on WhatsApp' : 'as a new mail under its own subject';
  return `To ${named ?? item.who ?? 'them'}, ${where}. ${channelWords(channel).cobrainSends ? 'You press Send.' : 'You send it yourself.'}`;
}

/** Open and Read go to what the label names and record nothing, so they still go somewhere under the keeper's view. */
const opensOnly = (o: Option): boolean => o.kind === 'open' || o.kind === 'read';

export type CardState = 'none' | 'park' | 'delegate' | 'why' | 'complete' | 'dismiss' | 'teach' | 'reply' | 'typed' | 'decline' | 'propose' | 'answered' | 'block' | 'booked' | 'handon';

export interface ItemCardProps {
  foundReadingBack?: boolean;
  item: PassItem;
  /** The store's principals, for the delegate picker; the signed-in principal is left out. */
  team: TeamMember[];
  self: string;
  /** Tier 2 draws a step smaller (U5, U21). */
  compact?: boolean;
  /** The rank square; the item page and the answer panel leave it off. */
  rank?: boolean;
  open: (path: string) => void;
  /** The item left the list (park, complete, dismiss, delegate, a decided option). */
  onGone: (id: string) => void;
  /** The item stays, as the API now describes it. */
  onUpdate: (item: PassItem) => void;
  /** A reasoned option that drafts something: the caller opens the draft panel (DraftTranslate). */
  onDraft: (item: PassItem, option: Option, result: DecideResult | null, instruction?: string | null) => void;
  /** D77: "Not private?" moves the item to the commercial store. Absent on the item page's aside. */
  onNotPrivate?: (item: PassItem) => void;
  /**
   * U34: triage holds a decision that takes the item out of the ten for eight seconds before it is sent, so the
   * undo strip can take it back with nothing sent. Given only by the triage page; elsewhere a decision posts at once.
   */
  hold?: (item: PassItem, body: Omit<DecideBody, 'store'>, words: string) => void;
  /** U33: the keyboard's current item on triage. */
  current?: boolean;
  /** U33: the page's keys reach the card through this handle. */
  onRegister?: (id: string, handle: CardHandle | null) => void;
  /** U33: print each option's number, the key that presses it (triage only). */
  numbers?: boolean;
  /** A held decision that failed when it was sent: the card came back and says why. */
  heldError?: string | null;
  /** A held decision that failed for a passing reason: the error line's "Try again" sends it again. */
  onRetry?: () => void;
  /** U41: the "Teach Cobrain" control, the graduate cap at the card's bottom right (triage). */
  teach?: boolean;
  /**
   * BUGS.md T65: a press that has been made already says so in the page's bottom bar, with its Undo, as an allocation card's
   * press does (Triage's sayAllocated). Given only by the triage page; elsewhere the card's own line carries the Undo.
   */
  say?: (x: { words: string; undo: (() => Promise<void>) | null; failed?: string }) => void;
  /** On the item page the headline is the page's own heading (h1). */
  heading?: boolean;
  /** U28's motion on triage: the card wipes out, arrives at ten, or its rank has changed. */
  motion?: 'leaving' | 'arriving' | 'moved' | null;
  children?: ReactNode;
}

/** What the keyboard can do to a card (U33). */
export interface CardHandle { choose: (n: number) => void; standing: (s: Standing) => void; teach: () => void }

/** U41: how an item was already done, sent as the note of the person's own Complete (module 04 Open 77). */
const HOW: Array<[string, string]> = [['email', 'Email'], ['call', 'A call'], ['whatsapp', 'WhatsApp'], ['teams', 'Teams'], ['in_person', 'In person'], ['other', 'Other']];
const TEACH_MAX = 500;
const HOW_PHRASE: Record<string, string> = { email: 'by email', call: 'on a call', whatsapp: 'on WhatsApp', teams: 'on Teams', in_person: 'in person', other: 'another way' };

/**
 * U34: the decisions whose outcome is certain to take the item out of the ten, so they can be held for undo. Leave it
 * with <name> joined them with actions contract row 11 ("Off your list, but still yours").
 */
/** Actions contract row 16: what Reply on a WhatsApp message says while WhatsApp is not live. */
export const WHATSAPP_REPLY_SOON = 'Replying on WhatsApp is under development.';
// "Nothing, awareness only" leaves the list too (actions contract row 13, B1839).
const HELD_KINDS = new Set(['assign', 'set_chase', 'leave_with', 'awareness']);
function holdable(o: Option): boolean {
  const p = o.params as Record<string, unknown> | undefined;
  // "Confirm what Cobrain read" (actions contract row 15) leaves the list as well.
  if (o.kind === 'other' && Array.isArray(p?.confirm)) return true;
  // "Nothing was said that needs keeping" on a call Cobrain did not get into (BUGS.md T26): the card goes for good.
  if (o.kind === 'other' && p?.nothingToKeep === true) return true;
  // A signed copy's "Close it as signed" and "Not this deal", and "Reopen it" after Cobrain's close (BUGS.md D43, D44):
  // each settles the card, so each gets the same few seconds to take it back as every other press that does.
  if (o.kind === 'other' && (p?.signedCopy !== undefined || p?.reopen === true)) return true;
  // "Take it yourself as a task" (actions contract row 12) and "Push it back an hour" (row 14) leave the list too, so they are
  // held as the others are.
  return HELD_KINDS.has(o.kind) || (o.kind === 'other' && (p?.not_me === true || p?.known === true || p?.take === true || typeof p?.minutes === 'number'));
}

/** U33: the letter that presses each standing action, shown on the keyboard's current card. */
const STANDING_KEY: Partial<Record<Standing, string>> = { park: 'P', complete: 'C', dismiss: 'D', delegate: 'G', why: 'W' };

/** The days between a delegation and now, for the silence line (D68). */
const daysSince = (iso: string): number => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));

/** The clock on the right of the meta row. */
export function whenText(when: PassItem['when']): string | null {
  if (!when) return null;
  if (when.text) return when.text;
  if (!when.at) return null;
  const w = formatWhen(when.at);
  return w.kind === 'relative' ? w.text : formatDate(w.iso);
}

/**
 * The first line of a card its call brought back (module 04 D288; D299 answers 2 and 3, Mike's words: "After your call with
 * Alex Roberts on 3 Oct: send Alex the revised usage term by Friday."). Built exactly as the server builds the sentence its
 * reason leads with (rank/rules.ts callStepLead): the call's day in UTC, the step's own words, ended as a sentence.
 */
export function callStepLead(step: Pick<CallStep, 'callee' | 'call_at' | 'line'>): string {
  const line = `After your call with ${step.callee} on ${dayMonth(new Date(step.call_at), 'utc')}: ${step.line.trim()}`;
  return /[.!?…]$/.test(line) ? line : `${line}.`;
}

/**
 * The words after the step's sentence, where they begin with it: the server leads the reason with that exact sentence
 * (rank/rules.ts withCallStep), and the card has already said it first. Anything else is returned as it is.
 */
export function withoutCallStep(text: string, step: CallStep | null): string {
  if (!step) return text;
  const lead = callStepLead(step);
  return text.startsWith(lead) ? text.slice(lead.length).trimStart() : text;
}

const toLocalInput = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * Why is this here? (ItemStates, ItemView; D30, D121): the reason line, the features the
 * explanation is built from, which brain judged it, and the four adjustments, each a labelled
 * example for the ranking. The same panel sits inside a card and on the item page.
 */
export function Why({ item, busy, onAdjust }: { item: PassItem; busy: boolean; onAdjust: (a: Adjustment) => void }) {
  const f = item.features;
  const place = item.tier === 1 ? String(item.rankNo) : item.tier === 2 ? 'the list' : item.tier === 4 ? 'awareness' : 'eleven and beyond';
  // Module 04 D299: the card says its call step first, so the reason goes on from it. A reason that is the step alone stays whole.
  const reason = withoutCallStep(item.reason, callStepOf(item)) || item.reason;
  return (
    <>
      <div className="why">
        Sits at <strong>{place}</strong> because: {reason}
        <ul>
          {f.waiting_on && f.waiting_on.length > 0 ? <li>Waiting on you: {f.waiting_on.join(', ')}</li> : null}
          {f.deadline?.at ? <li>Deadline: {formatDate(f.deadline.at)}{f.deadline.source ? `, from ${f.deadline.source}` : ''}</li> : null}
          {f.deal ? <li>Deal: {f.deal.stage ?? 'stage not inferred'}{f.deal.value !== null && f.deal.value !== undefined ? ` · ${f.deal.value.toLocaleString('en-GB')} ${f.deal.currency ?? ''}` : ''}</li> : null}
          {f.owner_direction ? <li>{f.owner_direction === 'user_owes' ? 'Owed by you' : f.owner_direction === 'owed_to_user' ? 'Owed to you' : f.owner_direction === 'mutual' ? 'Owed both ways' : 'Nothing owed either way'}</li> : null}
          {f.age_days !== null && f.age_days !== undefined ? <li>Age: {f.age_days} day{f.age_days === 1 ? '' : 's'}</li> : null}
          {f.silence?.passed ? <li>The reply window has passed{f.silence.expected_reply_by ? ` (expected by ${formatDate(f.silence.expected_reply_by)})` : ''}</li> : null}
          {f.exclusion ? <li>You were left off a thread that matters to you</li> : null}
          {f.imminent ? <li>Time-critical: it overrides the weighting</li> : null}
          {judgedBy(f.brain) ? <li>{judgedBy(f.brain)}</li> : null}
        </ul>
      </div>
      <div className="opts adjust">
        {ADJUSTMENTS.map((a) => (
          <ActionButton key={a.key} type="button" className="opt small" disabled={busy} onClick={() => onAdjust(a)}>{a.words}</ActionButton>
        ))}
      </div>
    </>
  );
}

export function ItemCard({ foundReadingBack, item, team, self, compact, rank = true, open, onGone, onUpdate, onDraft, onNotPrivate, hold, current, onRegister, numbers, heldError, onRetry, teach, say, motion, heading, children }: ItemCardProps) {
  const viewing = useViewing();
  const [state, setState] = useState<CardState>('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const [delegateTo, setDelegateTo] = useState('');
  const [how, setHow] = useState<string | null>(null);
  const [instead, setInstead] = useState('');
  /** The reply option whose box is open: pressing it has recorded nothing (D60). */
  const [chosen, setChosen] = useState<Option | null>(null);
  const [replyChoice, setReplyChoice] = useState<{ all: boolean; option?: Option } | null>(null);
  /** The refusal of the typed reply's last Send, kept under the box with the text still in it. */
  const [typedError, setTypedError] = useState<string | null>(null);
  const [dueAt, setDueAt] = useState(() => toLocalInput(parkPresets()[0]!.until ?? new Date(Date.now() + 86_400_000)));
  /** BUGS.md T65: the Undo of the block just booked, on the card's own line where the page gives no bottom bar. */
  const [unbook, setUnbook] = useState<(() => Promise<void>) | null>(null);
  const whyOpen = state === 'why';

  const decide = async (body: Omit<DecideBody, 'store' | 'explanationViewed'>, after?: (r: DecideResult) => void) => {
    setBusy(true); setError(null);
    try {
      const r = await triage.decide(item.id, { store: item.store, ...body, ...(whyOpen ? { explanationViewed: true } : {}) });
      after?.(r);
      return r;
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return null; }
      // The server's words, led in as the triage page and the item page lead them (B1626).
      const said = e instanceof ApiError || e instanceof Error ? e.message : String(e);
      setError(`That did not go through: ${said.replace(/\.$/, '')}.`);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const gone = () => onGone(item.id);

  const choose = async (o: Option) => {
    // Under the keeper's view only Open and Read go anywhere: they record nothing (actions contract row 7).
    if (viewing && !opensOnly(o)) return;
    if (hold && holdable(o)) { hold(item, { action: 'accept', optionKey: o.key, ...(whyOpen ? { explanationViewed: true } : {}) }, `${o.label}.`); return; }
    // Actions contract row 7: Open and Read go to exactly what the label names, and Join opens the meeting link. Pressing
    // records nothing, so the card stays until he deals with it ("Pressing does not clear the card").
    if (o.kind === 'open' || o.kind === 'read') { open(whereItGoes(item, o)); return; }
    // Actions contract row 15 (answer 22): "Correct opens those fields on the deal for you to change, and Cobrain learns from the
    // change." The item page opens with the stage and value to change; the press records nothing, the change is the record.
    if (o.kind === 'other' && (o.params as { correct?: unknown } | undefined)?.correct === true) { open(`/items/${encodeURIComponent(item.id)}?store=${encodeURIComponent(item.store)}&correct=deal`); return; }
    if (o.kind === 'join') {
      const link = joinLink(o);
      if (link) { window.open(link, '_blank', 'noopener'); setSaid(`Opened the meeting link: ${o.label}.`); } else open(whereItGoes(item, o));
      return;
    }
    // Actions contract row 2, "Reply": a box opens under the card; his own words go as typed, and a message starting
    // "Cobrain," is an instruction to draft (sendTyped). Nothing is recorded until the reply has gone.
    // Actions contract row 16 (Mike, answer 24: "Build to be ready for whatsapp, but whatsapp will be pushed to next week"; Open
    // item 1: "Tells user, under development"): until Cobrain sends on WhatsApp, Reply there says so and opens nothing. The
    // box and the send path are unchanged for the day it does.
    if (o.kind === 'reply' && replyChannelOf({ channel: typeof o.params?.channel === 'string' ? o.params.channel : null }) === 'whatsapp') { setSaid(WHATSAPP_REPLY_SOON); return; }
    // Accept already carries the instruction: show the voice draft before its combined Send.
    if (o.kind === 'draft' && o.params?.acceptBook === true) { writeThrough(o, null); return; }
    const replyDraft = writesForYou(o) && (o.kind === 'chase' || o.params?.purpose === 'reply' || o.params?.purpose === 'chase' || (o.kind === 'draft' && !o.params?.purpose));
    if (item.source && item.source.channel !== 'whatsapp' && (o.kind === 'reply' || replyDraft)) {
      setChosen(null); setState('none'); setReplyChoice({ all: false, option: o }); return;
    }
    if (o.kind === 'reply') { setChosen(o); setTypedError(null); setState('typed'); return; }
    // Actions contract row 8 (answer 11): Accept sends at once; Decline opens a box for a reason, optional, typed or
    // "Cobrain, ..."; Propose opens a time picker. Each answer goes to the organiser, and the card then says so.
    if (o.kind === 'accept') { await answerInvite(o, {}); return; }
    if (o.kind === 'decline') { setChosen(o); setTypedError(null); setState('decline'); return; }
    if (o.kind === 'propose_time') { setChosen(o); setTypedError(null); setState('propose'); return; }
    // Actions contract row 9 (answer 12, "You pick the time"): his free slots appear, and nothing is booked until he picks one.
    if (o.kind === 'block_time') { setChosen(o); setTypedError(null); setState('block'); return; }
    // Actions contract row 3, "Call <name>": the call is placed and Teams opens with it; the card stays for the call to decide.
    if (o.kind === 'call') { await callNow(o); return; }
    // Actions contract row 1, Cobrain's own options: the box opens with the option's words in it (module 04 D259 part
    // 2), and pressing through it asks for the draft. Nothing is recorded until the draft is sent (REQ-2026-09-24-05).
    if (writesForYou(o)) { setChosen(o); setState('reply'); return; }
    await decide({ action: 'accept', optionKey: o.key }, (r) => {
      if (r.draft) { onDraft(item, o, r); return; }
      // Milestone 10 (U25): a take, a give-back and a request keep the card where it is, in its new state.
      if (r.item) { onUpdate(r.item); setSaid(CLAIM_KINDS.has(o.kind as string) ? saidAfter(o.kind as string, r, o.label) : `Chosen: ${o.label}.`); return; }
      gone();
    });
  };

  /**
   * "Call <name>" (actions contract row 3): the tab Teams opens in is taken in the press itself (call/api.ts), the call
   * placed, and the tab sent to the meeting. The card stays, drawn anew, with how the other person was asked under it.
   * A call that could not be placed shows the server's words and places nothing.
   */
  const callNow = async (o: Option, video = false) => {
    const tab = openCallWindow();
    setBusy(true); setError(null);
    try {
      const r = await placeCall(item.id, { store: item.store, optionKey: o.key, ...(video ? { video: true } : {}), ...(whyOpen ? { explanationViewed: true } : {}) });
      if (!tab.go(r.joinUrl)) setError('Teams gave a link Cobrain does not open. Open the call from your Teams calendar.');
      if (r.item) onUpdate(r.item);
      setSaid(callSaid(video ? VIDEO_CALL : o.label, r));
    } catch (e: unknown) {
      tab.close();
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(e instanceof ApiError || e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * One of Cobrain's options, through the box (module 10 DESIGN B1553): the draft asked for with the words in the box.
   * Nothing is recorded here: the draft panel's Send records the decision once the draft has gone, so a draft never
   * sent leaves the card where it was (REQ-2026-09-24-05; D60).
   */
  const writeThrough = (o: Option, instruction: string | null) => {
    setChosen(null); setState('none');
    onDraft(item, o, null, instruction);
  };

  /**
   * "Reply to <name>": what he typed goes as typed (POST /api/triage/items/:id/reply), and the API records the
   * decision once it has gone. Then the card clears ("The card then clears"). A refusal keeps the box and its text.
   */
  /** Row 8: the answer goes to the organiser on this press; the card stays, saying what went and to whom. */
  const answerInvite = async (o: Option, extra: { note?: string; until?: string }) => {
    setBusy(true); setTypedError(null); setError(null);
    try {
      const r = await triage.decide(item.id, { store: item.store, action: 'accept', optionKey: o.key, ...extra, ...(whyOpen ? { explanationViewed: true } : {}) }) as DecideResult & InviteOutcome;
      setChosen(null); setState('answered');
      setSaid(answeredWord(o.kind, item.who, r, extra.note ?? null));
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      const said = e instanceof ApiError || e instanceof Error ? e.message : String(e);
      const why = `Not sent: ${said.replace(/\.$/, '')}.`;
      if (o.kind === 'accept') setError(why); else setTypedError(why);
    } finally {
      setBusy(false);
    }
  };

  /** Row 9: books the slot he picked; the card stays, saying when it is. */
  const book = async (o: Option, at: Date) => {
    setBusy(true); setTypedError(null);
    try {
      const r = await triage.decide(item.id, { store: item.store, action: 'accept', optionKey: o.key, until: at.toISOString(), ...(whyOpen ? { explanationViewed: true } : {}) }) as DecideResult & Parameters<typeof bookedWord>[0];
      setChosen(null); setState('booked');
      const words = bookedWord(r);
      setSaid(words);
      // BUGS.md T65: the booked line and its Undo in the bottom bar. Undo takes the block out of his calendar and puts the
      // card back as it was; a block nothing booked (no grant) has nothing to take out, so no Undo.
      const undo = booked(r) && r.decision?.id ? async () => {
        await undoBlock(item.store, r.decision.id);
        setUnbook(null); setSaid(null); setState('none');
      } : null;
      if (say) say({ words, undo, failed: 'The block is still in your calendar.' });
      else setUnbook(() => undo);
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      const said = e instanceof ApiError || e instanceof Error ? e.message : String(e);
      setTypedError(`Not booked: ${said.replace(/\.$/, '')}.`);
    } finally {
      setBusy(false);
    }
  };

  /** Row 8, "Cobrain, ..." in the decline box: the reason is written for him to read in the box before anything goes. */
  const writeReason = async (o: Option, instruction: string): Promise<string | null> => {
    setTypedError(null);
    // An invitation's card is its signal (the option names it); the draft lane finds no message behind the pass item's id.
    const sig = (o.params as { signalId?: unknown } | undefined)?.signalId;
    const on = typeof sig === 'string' && sig ? { signalId: sig } : { passItemId: item.id };
    try {
      const v = await replyDrafts.create({ store: item.store, ...on, purpose: 'reply', instruction: `A short reason for declining this invitation, one or two sentences, with no greeting and no sign-off${instruction ? `: ${instruction}` : ''}` });
      return v.draft.bodyEn;
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return null; }
      const said = e instanceof ApiError || e instanceof Error ? e.message : String(e);
      setTypedError(`Cobrain could not write it: ${said.replace(/\.$/, '')}. Type the reason instead.`);
      return null;
    }
  };

  const sendTyped = async (o: Option, text: string) => {
    // Row 2 (answer 28): "a message starting "Cobrain," is an instruction to draft". It is never sent as typed.
    const instruction = instructionOf(text);
    if (instruction !== null) { writeThrough(o, instruction || null); return; }
    setBusy(true); setTypedError(null);
    try {
      await typedReply.send(item.id, { store: item.store, optionKey: o.key, text });
      setChosen(null); setState('none');
      gone();
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      const said = e instanceof ApiError || e instanceof Error ? e.message : String(e);
      setTypedError(`Not sent: ${said.replace(/\.$/, '')}.`);
    } finally {
      setBusy(false);
    }
  };

  const park = async (until: Date) => {
    // BUGS.md T66: the strip says the time it comes back, as the choice named it ("Parked until 8 Oct 2026, 16:00.").
    if (hold) { hold(item, { action: 'park', until: until.toISOString() }, `Parked until ${dayMonthYearTime(until)}.`); return; }
    await decide({ action: 'park', until: until.toISOString() }, gone);
  };
  // U35: on triage Complete and Dismiss are one press each, held for undo, and the strip says what each teaches (D70).
  // U41: "Already done" is the person's own Complete, with how in the note; "None of these" is a note, and the card stays.
  // Focus goes to the first answer when the panel opens, so the keyboard lands where the eye does.
  useEffect(() => { if (state === 'teach') document.querySelector<HTMLButtonElement>(`[data-item="${CSS.escape(item.id)}"] [data-first-how]`)?.focus(); }, [state, item.id]);
  // BUGS.md T63: the Park and Delegate boxes close with Escape, as the reply boxes do (reply/TypedReply.tsx), wherever the focus
  // is; taken before the page's own keys, so the Escape that closes a box neither undoes a held press nor leaves the page.
  useEffect(() => {
    if (state !== 'park' && state !== 'delegate' && state !== 'handon') return;
    const on = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('dialog[open]')) return;
      e.preventDefault(); e.stopPropagation();
      setState('none');
    };
    window.addEventListener('keydown', on, true);
    return () => window.removeEventListener('keydown', on, true);
  }, [state]);
  const alreadyDone = async () => {
    if (!how) return;
    const body = { action: 'complete' as const, note: `already_done:${how}` };
    if (hold) { hold(item, body, 'Marked done: Cobrain will learn it missed how you did it.'); return; }
    await decide(body, gone);
  };
  const teachInstead = async () => {
    const t = instead.trim();
    if (!t) return;
    await decide({ action: 'other', note: `teach_option:${t}` }, () => { setSaid(`Noted: “${t.length > 90 ? `${t.slice(0, 90)}…` : t}”. Cobrain will learn from what you would do.`); setInstead(''); setState('none'); });
  };
  const finish = (action: 'complete' | 'dismiss') => {
    if (!hold) return false;
    hold(item, { action, ...(whyOpen ? { explanationViewed: true } : {}) }, action === 'complete' ? 'Completed: Cobrain will treat this as done.' : 'Dismissed: Cobrain will raise less like this.');
    return true;
  };

  const standing = async (s: Standing) => {
    setError(null);
    switch (s) {
      case 'park': setState(state === 'park' ? 'none' : 'park'); return;
      case 'delegate': setState(state === 'delegate' ? 'none' : 'delegate'); return;
      // Unfolding "Why is this here?" is an open (REQ-2026-09-24-21): said to the server, kept once a day, shown nowhere.
      case 'why': if (state !== 'why') triage.opened(item.id, { store: item.store, via: 'why' }); setState(state === 'why' ? 'none' : 'why'); return;
      case 'complete': if (!finish('complete')) setState(state === 'complete' ? 'none' : 'complete'); return;
      case 'dismiss': if (!finish('dismiss')) setState(state === 'dismiss' ? 'none' : 'dismiss'); return;
      case 'not_private': onNotPrivate?.(item); return;
    }
  };

  // Mike, 13 Oct 2026, on the keeper's view: "Needs to look exactly as the user sees it, no compromises". Every option the person
  // is offered is drawn, the ones that draft in their voice included; under a view they are locked (rails/ActionButton.tsx).
  const shownOptions = item.source ? item.optionsOffered.filter((o) => o.kind !== 'reply') : item.optionsOffered;
  const lead = shownOptions[0];
  const rest = shownOptions.slice(1);
  const offered = standingActions(item);

  // Module 04 B2220, BUGS.md T55 (Mike by pop-up, 6 Oct about 20:10 CEST, "Rotation until decided"): a card back by the
  // rotation asks "today, a date, hand on, or close" in place of Cobrain's options, each an existing action. Today is the
  // decide route's reorder_up with the note today, which keeps the card on today's list (rank/store.ts keptToday); A date is
  // Park until; Hand on presses the card's own Ask to take or Leave it with, and where the card offers neither, opens
  // Delegate; Close is Complete. Every other way of deciding stays in the row under them.
  const back = backOf(item);
  const handOn = item.optionsOffered.filter((o) => HAND_ON_KINDS.has(o.kind));
  const backChoose = async (c: BackChoice) => {
    setError(null);
    switch (c) {
      case 'Today': await decide({ action: 'reorder_up', note: 'today' }, (r) => { if (r.item) onUpdate(r.item); setState('none'); setSaid('Chosen: Today.'); }); return;
      case 'A date': await standing('park'); return;
      case 'Hand on': setState(handOn.length > 0 ? (state === 'handon' ? 'none' : 'handon') : (state === 'delegate' ? 'none' : 'delegate')); return;
      case 'Close': await standing('complete'); return;
    }
  };
  const backOpen: Partial<Record<BackChoice, boolean>> = { 'A date': state === 'park', 'Hand on': state === 'handon' || (handOn.length === 0 && state === 'delegate'), Close: state === 'complete' };

  // U33: the page's keys act on this card through the handle; a key for an action the card does not offer does nothing.
  useEffect(() => {
    if (!onRegister) return;
    onRegister(item.id, {
      choose: (n) => {
        if (busy) return;
        if (viewing) { const o = back ? undefined : shownOptions[n - 1]; if (o && opensOnly(o)) void choose(o); return; }
        if (back) { const c = BACK_CHOICES[n - 1]; if (c) void backChoose(c); return; }
        const o = shownOptions[n - 1]; if (o) void choose(o);
      },
      standing: (s) => { if (offered.includes(s) && !busy && (!viewing || s === 'why')) void standing(s); },
      teach: () => { if (teach && !busy && !viewing) setState((v) => (v === 'teach' ? 'none' : 'teach')); },
    });
    return () => onRegister(item.id, null);
  });
  // Milestone 10 (D217, U25): a shared item says who else it is with, whether anyone has taken it, and
  // past its reply window untaken it becomes the warning card naming the account owner (state 4).
  const shared = claimWords(item);
  const cls = [heading ? '' : 'in-list', compact ? 'qw-item' : '', shared?.state === 'warning' ? 'warn' : '', motion ?? ''].filter(Boolean).join(' ');
  // "Video call" beside every Call (REQ-2026-09-24-11): the same call, pressed as video. It takes no number, so the
  // keys still pick the options Cobrain offered.
  const videoBeside = (o: Option) => (o.kind === 'call'
    ? <OptionButton disabled={busy} onClick={() => void callNow(o, true)}>{VIDEO_CALL}</OptionButton>
    : null);
  // KIT follow-up: OptionButton needs aria-hidden on its number before these numbered options can use it.
  const num = (i: number) => (numbers && i < 9 ? <span className="n" aria-hidden="true">{i + 1}</span> : null);
  const quiet = item.delegation?.quiet ? daysSince(item.delegation.since) : null;
  // The live dot on a clock that is today or already past (VISUAL-SYSTEM, the clock plate).
  const live = item.when?.at ? new Date(item.when.at).getTime() < new Date(new Date().setHours(24, 0, 0, 0)).getTime() : false;
  // Only a fixed time is a plate: a deadline, or a time from today on. An earlier date with no deadline is an age ("10 days", "asked 3 Sep") and is meta text.
  const fixed = !!item.when?.at && (!!item.features.deadline?.at || new Date(item.when.at).getTime() >= new Date(new Date().setHours(0, 0, 0, 0)).getTime());
  const when = whenText(item.when);
  const headline = shared?.headline ?? item.headline;
  // Module 04 D299 answers 2 and 3: the step agreed on his call, which the card says first. Where the gist is the reason
  // (the server's fallback when there is no evidence to quote, rank/item-words.ts) it leads with the same sentence: said once.
  const step = callStepOf(item);
  const orgLine = useCompanyLine(item.store, compact ? null : item.organisation?.id);
  const ownGist = shared?.gist ?? item.gist;
  const gist = step && ownGist ? withoutCallStep(ownGist, step) || null : ownGist;
  const backMark = back ? <><Chip variant="back" data-back="">back</Chip>{' '}</> : null;
  const backSince = back ? <>{' '}<span className="back-since">{sinceWords(back.since)}</span></> : null;
  // Module 04 B2421 (Mike by pop-up, 8 Oct 2026, "All as recommended", its (a)): under a card back by the rotation, what arrived on
  // it since it was last in front of him; the newest and a count on the list, every line on the item page.
  const backChanged = changedWords(back, !!heading);
  const cardThread = useCardThread(item.store, item.source?.signalId ?? null);
  const newestMessage = cardThread?.messages.find((m) => m.id === cardThread.newestId) ?? cardThread?.messages.at(-1);
  const selfAddresses = cardThread?.selfAddresses ?? [];

  return (
    <CardFrame current={!!current} private={item.private} className={cls} data-item={item.id} data-tier={item.tier} data-claim={shared?.state ?? undefined} data-first={rank && item.tier === 1 && item.rankNo === 1 ? '' : undefined} aria-current={current ? 'true' : undefined}>
      {step ? <p className="changed" data-call-step="">{callStepLead(step)}</p> : null}
      <div className="meta">
        {item.private ? <Chip variant="private"><svg className="lock" viewBox="0 0 12 12" aria-hidden="true"><path d="M3.5 5V3.8a2.5 2.5 0 015 0V5h.5a1 1 0 011 1v4a1 1 0 01-1 1H3a1 1 0 01-1-1V6a1 1 0 011-1zm1 0h3V3.8a1.5 1.5 0 00-3 0z" /></svg>Private, only you</Chip> : null}
        <ClaimPills item={item} />
        {item.changesSince ? <Chip variant="back">Back from parking</Chip> : null}
        {step ? <Chip variant="back">After your call</Chip> : null}
        {item.channels.map((c) => <Chip key={c}>{kindWord(c)}</Chip>)}
        {item.who ? item.who.split(' · ').map((w) => <span key={w}>{w}</span>) : null}
        {foundReadingBack && <Chip>Found reading back</Chip>}
        {/* BUGS.md T75: the private pill above already says it; the badge of the same words is not drawn twice. */}
        {item.badge && !(item.private && item.badge === 'Private · only you') ? <Chip>{item.badge}</Chip> : null}
        {item.delegation ? <Chip>With {item.delegation.to} since {formatDate(item.delegation.since)}</Chip> : null}
        {when ? (fixed ? <span className="when clock">{live ? <i className="live" aria-hidden="true" /> : null}{when}</span> : <span className="when">{when}</span>) : null}
      </div>
      {/* The company the card is about, as its line (U353; the drawing's triage card), its name a link to the record (X30). */}
      {item.organisation && !compact ? <LineOrName id={item.organisation.id} name={item.organisation.name} line={orgLine} go={open} /> : null}
      <div className="headrow">
        {rank && item.tier === 1 ? <span className="rank" key={motion === 'moved' ? `r${item.rankNo}` : 'r'}>{item.rankNo}</span> : null}
        {/* U12: an item opens as a page of its own; in the list the headline is the way in. */}
        {/* BUGS.md T55: the preview's "(back) Genius Sports InPlay   not in front of you since <date>". */}
        {rank ? <p className="head">{backMark}<ActionButton reading type="button" className="head-link" onClick={() => open(`/items/${encodeURIComponent(item.id)}?store=${encodeURIComponent(item.store)}`)}>{headline}</ActionButton>{backSince}</p> : heading ? <h1 className="head">{backMark}{headline}{backSince}</h1> : <p className="head">{backMark}{headline}{backSince}</p>}
      </div>
      {backChanged ? <p className="changed" data-back-changed="">{backChanged}</p> : null}
      {/* BUGS.md T48 (Mike, 6 Oct: "Title and then what it is about and actions below it"; by pop-up "Keep both"): in the
          list a card is its title, the gist and its actions, so "While it was away" and the matter block are drawn only on the
          item page, where the card is the page's heading; the "Back from parking" pill stays on both. */}
      {item.changesSince && heading ? (
        <div className="changed"><strong>While it was away:</strong> {item.changesSince.lines.length > 0 ? item.changesSince.lines.join(' ') : 'nothing arrived.'}</div>
      ) : null}
      {item.source ? <CardMessage key={`${item.store}:${item.source.signalId}`} store={item.store} signalId={item.source.signalId} channel={item.source.channel} thread={cardThread} title={headline} open={!!replyChoice}>
        {replyChoice && newestMessage ? <ReplyBox key={`${replyChoice.all}:${replyChoice.option?.key ?? 'plain'}`} item={item} message={newestMessage} self={selfAddresses} all={replyChoice.all} {...(replyChoice.option ? { option: replyChoice.option } : {})}
          onBusy={setBusy} onCancel={() => setReplyChoice(null)} onSent={(names, after) => { setReplyChoice(null); setSaid(`Sent in the thread to ${names}${after ? `. ${after}` : ''}`); }} /> : null}
      </CardMessage> : gist && !compact && !(item.source === null && (item.subjectKind === 'signal' || item.subjectKind === 'commitment')) ? <p className="gist">{gist}</p> : null}
      {!compact ? <AlsoWith item={item} /> : null}
      {item.matter && !compact && heading ? (
        <div className="matter"><strong>A piece of work, not a reply.</strong> Filed under the matter <em>{item.matter.title}</em>, with {item.matter.openCommitments} open commitment{item.matter.openCommitments === 1 ? '' : 's'} on this matter.{item.matter.note ? ` ${item.matter.note[0]!.toUpperCase()}${item.matter.note.slice(1)}.` : ''}</div>
      ) : null}
      {item.delegation && quiet !== null && !compact ? (
        <>
          {/* D67, D68: delegated work stays yours; silence past the window warns you, in words and not by colour alone. */}
          <p className="silence"><span className="w"><svg className="warn-ico" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 .8l5.4 10H.6zM5.4 4.5v3h1.2v-3zm0 4v1.2h1.2V8.5z" /></svg>Silent for {quiet} day{quiet === 1 ? '' : 's'}</span> Nothing from {item.delegation.to} in any channel since it was handed on. Delegated work stays yours.</p>
          <div className="timeline3" aria-hidden="true"><i /><b className="gap" /><i className="w" /><b /><i /></div>
          <div className="timeline3-words"><span>{formatDate(item.delegation.since)}, handed to {item.delegation.to}</span><span>nothing since</span><span>due {formatDate(item.delegation.dueAt)}</span></div>
        </>
      ) : null}
      {children}
      {!compact ? <p className="q">What would you like to do?</p> : null}
      {back ? (
        <div className="opts" data-back-choices="">
          {BACK_CHOICES.map((c, i) => (
            <ActionButton key={c} type="button" className="opt" disabled={busy} onClick={() => void backChoose(c)}
                    {...(c === 'A date' || c === 'Hand on' || c === 'Close' ? { 'aria-expanded': backOpen[c] === true } : {})}>{num(i)}<span className="l">{c}</span></ActionButton>
          ))}
        </div>
      ) : (
      <div className="opts">
        {lead ? <ActionButton reading={opensOnly(lead)} type="button" className="opt lead" disabled={busy || state === 'answered' || state === 'booked'} onClick={() => void choose(lead)}>{num(0)}<span className="l">{lead.label}</span>{lead.note ? <small>{lead.note}</small> : null}</ActionButton> : null}
        {lead ? videoBeside(lead) : null}
        {rest.map((o, i) => <Fragment key={o.key}><ActionButton reading={opensOnly(o)} type="button" className="opt" disabled={busy || state === 'answered' || state === 'booked'} onClick={() => void choose(o)}>{num(i + 1)}<span className="l">{o.label}</span>{o.note ? <small>{o.note}</small> : null}</ActionButton>{videoBeside(o)}</Fragment>)}
      </div>
      )}
      {item.source && newestMessage ? <ActionRow variant="replies" actions={[
        { key: 'reply', label: 'Reply', disabled: busy, onClick: () => { if (item.source?.channel === 'whatsapp') setSaid(WHATSAPP_REPLY_SOON); else { setChosen(null); setState('none'); setReplyChoice({ all: false }); } } },
        ...(item.source.channel === 'email' && canReplyAll(newestMessage, selfAddresses) ? [{ key: 'reply-all', label: 'Reply all', disabled: busy, onClick: () => { setChosen(null); setState('none'); setReplyChoice({ all: true }); } }] : []),
      ]} /> : null}
      <ActionRow variant="standing" actions={[
        ...offered.map((s) => ({ key: s, opensOnly: s === 'why', className: [state === s ? 'on' : '', s === 'not_private' ? 'notpriv' : ''].filter(Boolean).join(' '),
          'aria-pressed': state === s, disabled: busy, onClick: () => void standing(s),
          label: <>{STANDING_WORDS[s]}{numbers && STANDING_KEY[s] ? <kbd className={current ? 'sk' : 'sk off'} aria-hidden="true">{STANDING_KEY[s]}</kbd> : null}</>,
        })),
        ...(teach ? [{ key: 'teach', className: state === 'teach' ? 'teach-btn on' : 'teach-btn', 'aria-expanded': state === 'teach', 'aria-label': 'Teach Cobrain', disabled: busy,
          onClick: () => setState(state === 'teach' ? 'none' : 'teach'), label: <>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4 2 9l10 5 10-5-10-5z" /><path d="M6 11.5V16c0 1.2 2.7 2.5 6 2.5s6-1.3 6-2.5v-4.5" /><path d="M22 9v5" /></svg>
            <span className="teach-word" aria-hidden="true">Teach</span>
            {current ? <kbd className="sk" aria-hidden="true">T</kbd> : null}
            <span className="tip" aria-hidden="true">Teach Cobrain</span>
          </>,
        }] : []),
      ]} />

      {state === 'typed' && chosen ? (
        <TypedReply
          going={goingWord(item, chosen)}
          busy={busy}
          error={typedError}
          onSend={(text) => void sendTyped(chosen, text)}
          onCancel={() => { setChosen(null); setTypedError(null); setState('none'); }}
        />
      ) : null}

      {state === 'decline' && chosen ? (
        <DeclineBox
          going={`To ${item.who ?? 'the organiser'}, with your decline. It goes when you press it.`}
          busy={busy}
          error={typedError}
          onDecline={(reason) => void answerInvite(chosen, reason ? { note: reason } : {})}
          onWrite={(instruction) => writeReason(chosen, instruction)}
          onCancel={() => { setChosen(null); setTypedError(null); setState('none'); }}
        />
      ) : null}

      {state === 'propose' && chosen ? (
        <ProposeBox
          going={`To ${item.who ?? 'the organiser'}, as a new time. It goes when you press it.`}
          startsAt={item.when?.at ?? null}
          busy={busy}
          error={typedError}
          onPropose={(at) => void answerInvite(chosen, { until: at.toISOString() })}
          onCancel={() => { setChosen(null); setTypedError(null); setState('none'); }}
        />
      ) : null}

      {state === 'block' && chosen ? (
        <BlockBox
          store={item.store}
          passItemId={item.id}
          optionKey={chosen.key}
          busy={busy}
          error={typedError}
          onBook={(at) => void book(chosen, at)}
          onCancel={() => { setChosen(null); setTypedError(null); setState('none'); }}
        />
      ) : null}

      {state === 'reply' && chosen ? (
        <PromptBox
          going={goingWord(item, chosen)}
          seed={isVoiceOption(chosen) ? null : chosen.label}
          busy={busy}
          onWrite={(instruction) => void writeThrough(chosen, instruction)}
          onCancel={() => { setChosen(null); setState('none'); }}
        />
      ) : null}

      {state === 'park' ? (
        <div className="state" data-state="park">
          <p className="rule">Parking takes a time, never a reason.</p>
          <div className="choices">
            {parkPresets().map((p) => p.until
              ? <ActionButton key={p.key} type="button" className="choice" disabled={busy} onClick={() => void park(p.until!)}>{p.label}</ActionButton>
              : (
                <div key={p.key} className="choice pick">
                  <span>Pick a time…</span>
                  <ActionInput type="datetime-local" aria-label="park until" value={pick} min={toLocalInput(new Date())} onChange={(e) => setPick(e.target.value)} />
                  <ActionButton type="button" className="btn small" disabled={busy || !pick} onClick={() => void park(new Date(pick))}>Park</ActionButton>
                </div>
              ))}
          </div>
          <div className="teach-block">
            <ActionButton reading type="button" className="link" disabled={busy} onClick={() => setState('none')}>Cancel</ActionButton>
          </div>
        </div>
      ) : null}

      {state === 'handon' ? (
        <div className="state" data-state="handon">
          <div className="opts">
            {handOn.map((o) => <OptionButton key={o.key} note={o.note} disabled={busy} onClick={() => { setState('none'); void choose(o); }}>{o.label}</OptionButton>)}
          </div>
          <div className="teach-block">
            <ActionButton reading type="button" className="link" disabled={busy} onClick={() => setState('none')}>Cancel</ActionButton>
          </div>
        </div>
      ) : null}

      {state === 'delegate' ? (
        <div className="state" data-state="delegate">
          <p className="rule">Delegated work stays yours, with a timeline. Silence before the date warns you, not them.</p>
          <div className="delegate-row">
            <label className="field"><span className="note">To</span>
              <ActionSelect aria-label="delegate to" value={delegateTo} onChange={(e) => setDelegateTo(e.target.value)}>
                <option value="">Choose a person…</option>
                {team.filter((t) => t.principalId !== self).map((t) => <option key={t.principalId} value={t.principalId}>{t.name}</option>)}
              </ActionSelect>
            </label>
            <label className="field"><span className="note">Due</span><ActionInput type="datetime-local" aria-label="due" value={dueAt} onChange={(e) => setDueAt(e.target.value)} /></label>
            <ActionButton type="button" className="btn small" disabled={busy || !delegateTo || !dueAt} onClick={() => void decide({ action: 'delegate', delegateTo, dueAt: new Date(dueAt).toISOString() }, gone)}>Delegate</ActionButton>
          </div>
          <div className="teach-block">
            <ActionButton reading type="button" className="link" disabled={busy} onClick={() => setState('none')}>Cancel</ActionButton>
          </div>
        </div>
      ) : null}

      {state === 'complete' || state === 'dismiss' ? (
        <div className="state" data-state={state}>
          <p className="rule">Both are always offered, and they teach the ranking opposite things. Neither sends anything.</p>
          <div className="two">
            <ActionButton type="button" className={state === 'complete' ? 'choice on' : 'choice'} disabled={busy} onClick={() => void decide({ action: 'complete' }, gone)}><strong>Complete</strong><span className="note">Done. Cobrain was right to raise it.</span></ActionButton>
            <ActionButton type="button" className={state === 'dismiss' ? 'choice on' : 'choice'} disabled={busy} onClick={() => void decide({ action: 'dismiss' }, gone)}><strong>Dismiss</strong><span className="note">Not for me. It should not have been raised.</span></ActionButton>
          </div>
        </div>
      ) : null}

      {state === 'why' ? (
        <div className="state" data-state="why">
          <Why item={item} busy={busy} onAdjust={(a) => void decide(a.label ? { action: 'other', label: a.label } : { action: 'other', note: a.note }, () => setSaid('Noted. Every adjustment trains the ranking.'))} />
        </div>
      ) : null}

      {state === 'teach' ? (
        <div className="state teach" data-state="teach">
          <p className="teach-title">Teach Cobrain</p>
          <div className="teach-block" role="group" aria-labelledby={`done-${item.id}`}>
            <p className="teach-h" id={`done-${item.id}`}>Already done. How?</p>
            <div className="opts">
              {HOW.map(([k, w], i) => <ActionButton key={k} type="button" data-first-how={i === 0 ? '' : undefined} className={how === k ? 'opt how on' : 'opt how'} aria-pressed={how === k} disabled={busy} onClick={() => setHow(k)}>{w}</ActionButton>)}
            </div>
            <div className="teach-row">
              <ActionButton type="button" className="opt lead" disabled={busy || !how} onClick={() => void alreadyDone()}>Mark it done</ActionButton>
              <span className="note">{how ? `Closes it and tells Cobrain it was already done ${HOW_PHRASE[how] ?? 'another way'}, so it learns what it missed; undo for eight seconds.` : 'Choose how first.'}</span>
            </div>
          </div>
          <div className="teach-block teach-instead">
            <label className="teach-h" htmlFor={`instead-${item.id}`}>None of the options above. I'd…</label>
            <div className="teach-row">
              <ActionInput id={`instead-${item.id}`} className="teach-input" type="text" maxLength={TEACH_MAX} value={instead} placeholder="what you would do instead" onChange={(e) => setInstead(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void teachInstead(); } }} />
              <ActionButton type="button" className="opt" disabled={busy || !instead.trim()} onClick={() => void teachInstead()}>Teach it</ActionButton>
            </div>
          </div>
          <ActionButton reading type="button" className="link inline teach-close" onClick={() => setState('none')}>Close</ActionButton>
        </div>
      ) : null}

      {said ? <p className="note said">{said}{unbook && state === 'booked' ? <>{' '}<ActionButton type="button" className="link inline" disabled={busy} onClick={() => { setBusy(true); setError(null); unbook().catch((e: unknown) => { if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; } setError(`Not undone: ${(e instanceof Error ? e.message : String(e)).replace(/\.$/, '')}. The block is still in your calendar.`); }).finally(() => setBusy(false)); }}>Undo</ActionButton></> : null}</p> : null}
      {error ?? heldError ? <p className="error" role="alert">{error ?? heldError}{heldError ? <> {onRetry ? <ActionButton type="button" className="link inline" onClick={onRetry}>Try again</ActionButton> : <ActionButton reading type="button" className="link inline" onClick={() => open(`/items/${encodeURIComponent(item.id)}?store=${encodeURIComponent(item.store)}`)}>Open it to see where it stands</ActionButton>}</> : null}</p> : null}
    </CardFrame>
  );
}
